import { Globe, Rocket, ChevronDown } from "lucide-react";

const WEB_STEPS = [
  {
    title: "Download your project",
    body: (
      <>
        Click <strong>Download</strong> in the toolbar above. Unzip the folder on your
        computer — it includes a Vite + React app with <code className="code-inline">npm run dev</code>{" "}
        and <code className="code-inline">npm run build</code> scripts.
      </>
    ),
  },
  {
    title: "Install Node.js and test locally",
    body: (
      <>
        Install{" "}
        <a
          href="https://nodejs.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-consensus-300 hover:underline"
        >
          Node.js 18+
        </a>{" "}
        if you do not have it. Open a terminal in the project folder and run:
        <pre className="deploy-code">{`npm install
npm run dev`}</pre>
        Open the local URL shown in the terminal (usually{" "}
        <code className="code-inline">http://localhost:5173</code>). Fix anything that
        breaks before deploying.
      </>
    ),
  },
  {
    title: "Create a production build",
    body: (
      <>
        When you are happy with the site, run:
        <pre className="deploy-code">npm run build</pre>
        Vite writes optimized static files to the <code className="code-inline">dist/</code>{" "}
        folder. That folder is what you upload to the internet.
      </>
    ),
  },
  {
    title: "Deploy to a hosting provider (pick one)",
    body: (
      <div className="space-y-4">
        <div>
          <p className="font-medium text-white/80">Netlify (easy drag-and-drop)</p>
          <ol className="deploy-list">
            <li>
              Sign up at{" "}
              <a
                href="https://netlify.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-consensus-300 hover:underline"
              >
                netlify.com
              </a>
              .
            </li>
            <li>
              Drag the entire <code className="code-inline">dist/</code> folder onto the
              Netlify dashboard, or connect your GitHub repo.
            </li>
            <li>
              If using Git: build command <code className="code-inline">npm run build</code>
              , publish directory <code className="code-inline">dist</code>.
            </li>
            <li>Netlify gives you a free URL like <code className="code-inline">your-app.netlify.app</code>.</li>
          </ol>
        </div>
        <div>
          <p className="font-medium text-white/80">Vercel</p>
          <ol className="deploy-list">
            <li>
              Push your project to GitHub, then import it at{" "}
              <a
                href="https://vercel.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-consensus-300 hover:underline"
              >
                vercel.com
              </a>
              .
            </li>
            <li>Framework preset: <strong>Vite</strong>. Deploy — Vercel detects the settings automatically.</li>
          </ol>
        </div>
        <div>
          <p className="font-medium text-white/80">Cloudflare Pages</p>
          <ol className="deploy-list">
            <li>
              Connect a Git repo at{" "}
              <a
                href="https://pages.cloudflare.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-consensus-300 hover:underline"
              >
                Cloudflare Pages
              </a>
              .
            </li>
            <li>
              Build command <code className="code-inline">npm run build</code>, output directory{" "}
              <code className="code-inline">dist</code>.
            </li>
          </ol>
        </div>
      </div>
    ),
  },
  {
    title: "Add a custom domain (optional)",
    body: (
      <>
        Buy a domain from Namecheap, Google Domains, Cloudflare, etc. In your host&apos;s
        dashboard, add the domain and follow their DNS instructions (usually a CNAME record
        pointing to your host). SSL/HTTPS is included free on Netlify, Vercel, and
        Cloudflare.
      </>
    ),
  },
];

const MOBILE_STEPS = [
  {
    title: "Download your Expo project",
    body: (
      <>
        Click <strong>Download</strong> above. The zip includes <code className="code-inline">App.js</code>
        , <code className="code-inline">app.json</code>, bundle IDs, and icon assets you configured.
        Unzip it on your computer.
      </>
    ),
  },
  {
    title: "Test on your phone first (Expo Go)",
    body: (
      <>
        Install <strong>Expo Go</strong> on iOS or Android. Scan the QR code above (tap{" "}
        <strong>Refresh QR</strong> after code changes). This lets you preview without publishing
        to any store. Fix bugs before you build for production.
      </>
    ),
  },
  {
    title: "Install tools and run locally",
    body: (
      <>
        Install{" "}
        <a
          href="https://nodejs.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-consensus-300 hover:underline"
        >
          Node.js 18+
        </a>
        . In the project folder:
        <pre className="deploy-code">{`npm install
npx expo start`}</pre>
        You can also open the project in{" "}
        <a
          href="https://snack.expo.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="text-consensus-300 hover:underline"
        >
          Expo Snack
        </a>{" "}
        via <strong>Open in Expo Snack</strong> above.
      </>
    ),
  },
  {
    title: "Set up Expo Application Services (EAS) for store builds",
    body: (
      <>
        Store releases require compiled native apps. Expo&apos;s free tier covers development
        builds; production builds use{" "}
        <a
          href="https://expo.dev/eas"
          target="_blank"
          rel="noopener noreferrer"
          className="text-consensus-300 hover:underline"
        >
          EAS Build
        </a>
        .
        <pre className="deploy-code">{`npm install -g eas-cli
eas login
eas build:configure`}</pre>
        Confirm the bundle IDs in <code className="code-inline">app.json</code> match what you
        entered above. Add a 1024×1024 PNG icon in <code className="code-inline">assets/</code> if
        you have not already.
      </>
    ),
  },
  {
    title: "Build for iOS (Apple App Store)",
    body: (
      <div className="space-y-2">
        <ol className="deploy-list">
          <li>
            Enroll in the{" "}
            <a
              href="https://developer.apple.com/programs/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-consensus-300 hover:underline"
            >
              Apple Developer Program
            </a>{" "}
            ($99/year).
          </li>
          <li>
            Run <code className="code-inline">eas build --platform ios</code> and follow the
            prompts (credentials can be managed by EAS).
          </li>
          <li>
            When the build finishes, submit:{" "}
            <code className="code-inline">eas submit --platform ios</code>
          </li>
          <li>
            In{" "}
            <a
              href="https://appstoreconnect.apple.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-consensus-300 hover:underline"
            >
              App Store Connect
            </a>
            , create the app listing (screenshots, description, privacy policy), then submit
            for review. Review usually takes 1–3 days.
          </li>
        </ol>
      </div>
    ),
  },
  {
    title: "Build for Android (Google Play)",
    body: (
      <div className="space-y-2">
        <ol className="deploy-list">
          <li>
            Create a developer account at{" "}
            <a
              href="https://play.google.com/console"
              target="_blank"
              rel="noopener noreferrer"
              className="text-consensus-300 hover:underline"
            >
              Google Play Console
            </a>{" "}
            (one-time $25 fee).
          </li>
          <li>
            Run <code className="code-inline">eas build --platform android</code> to produce an
            AAB file.
          </li>
          <li>
            Submit: <code className="code-inline">eas submit --platform android</code>, or upload
            the AAB manually in Play Console.
          </li>
          <li>
            Complete the store listing (icon, feature graphic, screenshots, content rating
            questionnaire), then roll out to internal testing, closed testing, or production.
          </li>
        </ol>
      </div>
    ),
  },
  {
    title: "After launch",
    body: (
      <>
        Bump <code className="code-inline">version</code> in <code className="code-inline">app.json</code>{" "}
        for each update, rebuild with EAS, and resubmit. Keep privacy policies and store
        screenshots up to date. For apps that collect user data, you may need a privacy policy
        URL in both stores.
      </>
    ),
  },
];

export default function DeployGuide({ mode }) {
  const isWeb = mode === "react-web";
  const steps = isWeb ? WEB_STEPS : MOBILE_STEPS;
  const Icon = isWeb ? Globe : Rocket;
  const title = isWeb
    ? "Deploy your website to the internet"
    : "Publish your mobile app to the App Store & Google Play";

  return (
    <div className="px-4 md:px-10 py-3 border-b border-white/5 bg-ink-900/20 shrink-0">
      <details className="group">
        <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-medium text-white/90 select-none">
          <Icon className="h-4 w-4 text-consensus-400 shrink-0" />
          <span className="flex-1">{title}</span>
          <ChevronDown className="h-4 w-4 text-white/40 transition group-open:rotate-180" />
        </summary>

        <div className="mt-4 space-y-5 max-w-3xl max-h-[min(40vh,420px)] overflow-y-auto pr-1">
          <p className="text-[12px] text-white/50 leading-relaxed">
            {isWeb
              ? "Your downloaded project is a standard Vite + React site. Follow these steps to go from preview to a live URL anyone can visit."
              : "Your download is an Expo (React Native) project. Test with Expo Go first, then use EAS Build to ship to Apple's App Store and Google Play."}
          </p>

          <ol className="space-y-5">
            {steps.map((step, i) => (
              <li key={step.title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-consensus-500/15 border border-consensus-500/30 text-[11px] font-semibold text-consensus-200">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <h4 className="text-sm font-medium text-white/85">{step.title}</h4>
                  <div className="text-[12px] text-white/55 leading-relaxed deploy-body">
                    {step.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </details>

      <style>{`
        .deploy-code {
          margin-top: 0.5rem;
          padding: 0.65rem 0.75rem;
          border-radius: 0.5rem;
          background: rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(255, 255, 255, 0.08);
          font-family: ui-monospace, monospace;
          font-size: 11px;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.75);
          overflow-x: auto;
          white-space: pre-wrap;
        }
        .deploy-list {
          margin-top: 0.35rem;
          padding-left: 1.1rem;
          list-style: decimal;
        }
        .deploy-list li {
          margin-top: 0.35rem;
        }
        .deploy-body .code-inline,
        .code-inline {
          padding: 0.1rem 0.35rem;
          border-radius: 0.25rem;
          background: rgba(255, 255, 255, 0.06);
          font-family: ui-monospace, monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
        }
        details > summary::-webkit-details-marker {
          display: none;
        }
      `}</style>
    </div>
  );
}
